import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import { forceString, isDateAfterToday } from 'src/utils/datetime';
import { useGetCandidateDetails } from 'src/pages/CandidatesDetailsPage/api';

import CandidateInfo from '../CandidateInfo/CandidateInfo';
import { CandidateTabProps } from '../CandidateShiftsTab';

import messages from './messages';

export default function CandidatePersonalInfoTab({ uuid }: CandidateTabProps) {
  const { formatMessage } = useIntl();

  const { data: candidate } = useGetCandidateDetails({
    uuid: uuid,
    enabled: false,
  });

  const matriculationFiles = useMemo(() => {
    const validMatriculationFiles = candidate?.data?.matriculationFiles?.filter(
      (file) =>
        file.status.code === 'approved' &&
        file.validUntil &&
        isDateAfterToday(file.validUntil),
    );

    return {
      first: validMatriculationFiles?.[0],
      next: validMatriculationFiles?.slice(1),
    };
  }, [candidate?.data?.matriculationFiles]);

  const workPermitFiles = useMemo(() => {
    const validWorkPermitFiles = candidate?.data?.workPermitFiles?.filter(
      (file) =>
        file.status.code === 'approved' &&
        file.validUntil &&
        isDateAfterToday(file.validUntil),
    );

    return {
      first: validWorkPermitFiles?.[0],
      next: validWorkPermitFiles?.slice(1),
    };
  }, [candidate?.data?.workPermitFiles]);

  if (!candidate?.data) return null;

  const {
    birthCountry,
    birthPlace,
    birthPostCode,
    gender,
    birthday,
    education,
  } = candidate.data;

  return (
    <Grid container spacing={6} p={2}>
      <Grid
        size={{ xs: 12, md: 4 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.basicWorkabilityTitle)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.activeMatriculationUntil)}
          value={
            matriculationFiles?.first?.validUntil
              ? forceString(matriculationFiles?.first?.validUntil)
              : '-'
          }
        />
        {matriculationFiles?.next &&
          matriculationFiles?.next.map((file) => (
            <CandidateInfo
              key={file.id}
              label={formatMessage(messages.nextMatriculationFrom)}
              value={forceString(file.validFrom)}
            />
          ))}

        <CandidateInfo
          label={formatMessage(messages.workPermitValidUntil)}
          value={
            workPermitFiles?.first?.validUntil
              ? forceString(workPermitFiles?.first?.validUntil)
              : '-'
          }
        />

        {workPermitFiles?.next &&
          workPermitFiles?.next.map((file) => (
            <CandidateInfo
              key={file.id}
              label={formatMessage(messages.nextMatriculationFrom)}
              value={forceString(file.validFrom)}
            />
          ))}
      </Grid>
      <Grid
        size={{ xs: 12, md: 4 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.personalTitle)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.dateOfBirth)}
          value={forceString(birthday)}
        />
        <CandidateInfo
          label={formatMessage(messages.placeOfBirth)}
          value={birthPlace}
        />
        <CandidateInfo
          label={formatMessage(messages.countryOfBirth)}
          value={birthCountry?.name}
        />
        <CandidateInfo
          label={formatMessage(messages.placeOfBirthPostalCode)}
          value={birthPostCode}
        />
        <CandidateInfo
          label={formatMessage(messages.gender)}
          value={gender?.name}
        />
      </Grid>
      <Grid
        size={{ xs: 12, md: 4 }}
        container
        spacing={4}
        flexDirection="column"
      >
        <Typography variant="h4" fontSize={20}>
          {formatMessage(messages.educationTitle)}
        </Typography>
        <CandidateInfo
          label={formatMessage(messages.schoolUniversity)}
          value={education?.university}
        />
        <CandidateInfo
          label={formatMessage(messages.fieldOfStudy)}
          value={education?.fieldStudy}
        />
        <CandidateInfo
          label={formatMessage(messages.cityOfUniversityCountry)}
          value={education?.address?.town}
        />
        <CandidateInfo
          label={formatMessage(messages.highestSchoolDegree)}
          value={education?.highestSchoolDegree?.name}
        />
        <CandidateInfo
          label={formatMessage(messages.highestLevelOfEducation)}
          value={education?.educationLevel?.name}
        />
      </Grid>
    </Grid>
  );
}
